import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, RefreshCw, AlertCircle, Server, LayoutGrid, Brain, Database, Wrench, Globe, Trash2 } from 'lucide-react';

type ServiceStatus = 'ok' | 'configured' | 'missing' | 'error';

// Дистрибуции (integration_apps) с https://instashop.one/shop/{storeId}/distribution.
// Список зашит в коде — живого API для получения дистрибуций у backend_admin нет (Pimmer требует
// Bearer-токен авторизованного пользователя, а не serviceToken). При появлении новой дистрибуции
// на pimmer.dev — добавить сюда вручную.
const DISTRIBUTION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '70a147e9-bdb8-4f68-87ce-cb022a38cd63', label: 'Bereke Bank' },
  { value: 'fe15455f-7938-4cca-97b8-6a523b357799', label: 'Pimmer' },
  { value: 'e9c81297-5c5b-4433-af15-530e712bda8f', label: 'Nambaone.app' },
  { value: 'e5a15a47-f6ec-497b-b120-a83bc070304c', label: 'Onay.kz' },
  { value: 'e005ff3d-d35d-4c4d-bab4-7caca90e6729', label: 'Скоро: Oriflame' },
  { value: 'd2127edb-6395-412a-8d43-b8aa246b5afd', label: 'METRO Cash & Carry' },
  { value: 'c71283a2-028b-4862-93d9-a36377277237', label: 'Instashop Express' },
  { value: 'ac445e4c-2525-4fc2-93be-d7800a52b7f9', label: 'Test' },
  { value: '8b020c7a-99de-45b6-a1f6-e4b563dcc3e7', label: 'Tele2.kz' },
  { value: '762ba4ae-2f72-4636-b310-9ae202701f30', label: 'Homecredit.kz' },
  { value: '0199e7b2-e1a3-72f0-9806-d0e213985397', label: 'QR' },
  { value: '626872bd-6859-4eb9-920e-26f254a4044a', label: 'SDK' },
  { value: '602f98ab-32c3-45f8-abf1-07c37416e2ea', label: 'Greenway' },
  { value: '5da61bd3-9bf7-45cf-b5e1-4c66e39c24d6', label: 'Instashop for Business' },
  { value: '35a7a418-bd2c-4805-ba03-28dd8cbd2840', label: 'Instashop.kz' },
  { value: '0324e7e0-db0c-442e-8863-1497bd12d94b', label: 'A-Store' },
  { value: '0302f53c-4c48-4e7f-94d8-b28ba27fb419', label: 'WEB Pimmer' },
  { value: '019ee8b9-1fdf-73d9-8e82-9752e95480de', label: 'M-Mart' },
  { value: '019a6df3-89fa-736c-82de-7284b1e4065f', label: 'Тест для бота' },
  { value: '88a64319-729d-4696-8961-84b26a265485', label: 'Блиц-меню' },
  { value: '5bbf5224-5f70-4f2c-a42c-f6b2cf91897f', label: 'Блиц-каталог' },
];

function StatusBadge({ status }: { status: ServiceStatus }) {
  const config: Record<ServiceStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
    ok: { label: 'OK', variant: 'default' },
    configured: { label: 'Настроен', variant: 'default' },
    missing: { label: 'Не настроен', variant: 'secondary' },
    error: { label: 'Ошибка', variant: 'destructive' },
  };
  const { label, variant } = config[status] ?? config.error;
  return <Badge variant={variant}>{label}</Badge>;
}

function formatDate(s: string | null | undefined) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}

export function ServicesStatus() {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getServicesStatus>> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voyageSettings, setVoyageSettings] = useState<Awaited<ReturnType<typeof api.getVoyageSettings>> | null>(null);
  const [isVoyageLoading, setIsVoyageLoading] = useState(false);
  const [isVoyageSaving, setIsVoyageSaving] = useState(false);
  const [isVoyageTesting, setIsVoyageTesting] = useState(false);
  const [voyageExpanded, setVoyageExpanded] = useState(false);
  const [voyageApiKeyInput, setVoyageApiKeyInput] = useState('');
  const [voyageSaveMsg, setVoyageSaveMsg] = useState<string | null>(null);
  const [notionSettings, setNotionSettings] = useState<Awaited<ReturnType<typeof api.getNotionSettings>> | null>(null);
  const [isNotionLoading, setIsNotionLoading] = useState(false);
  const [isNotionSaving, setIsNotionSaving] = useState(false);
  const [isNotionTesting, setIsNotionTesting] = useState(false);
  const [notionExpanded, setNotionExpanded] = useState(false);
  const [notionTokenInput, setNotionTokenInput] = useState('');
  const [notionSaveMsg, setNotionSaveMsg] = useState<string | null>(null);
  const [openaiSettings, setOpenaiSettings] = useState<Awaited<ReturnType<typeof api.getOpenAISettings>> | null>(null);
  const [isOpenAILoading, setIsOpenAILoading] = useState(false);
  const [isOpenAISaving, setIsOpenAISaving] = useState(false);
  const [isOpenAITesting, setIsOpenAITesting] = useState(false);
  const [openaiExpanded, setOpenaiExpanded] = useState(false);
  const [openaiApiKeyInput, setOpenaiApiKeyInput] = useState('');
  const [openaiSaveMsg, setOpenaiSaveMsg] = useState<string | null>(null);
  const [mysqlMaintenanceExpanded, setMysqlMaintenanceExpanded] = useState(false);
  const [clearTarget, setClearTarget] = useState<null | 'integration-requests' | 'users-and-stores' | 'subscriptions'>(null);
  const [clearConfirmInput, setClearConfirmInput] = useState('');
  const [isClearing, setIsClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState<string | null>(null);
  const [pimmerSettings, setPimmerSettings] = useState<Awaited<ReturnType<typeof api.getPimmerSettings>> | null>(null);
  const [isPimmerLoading, setIsPimmerLoading] = useState(false);
  const [isPimmerSaving, setIsPimmerSaving] = useState(false);
  const [isPimmerTesting, setIsPimmerTesting] = useState(false);
  const [pimmerExpanded, setPimmerExpanded] = useState(false);
  const [pimmerTokenInput, setPimmerTokenInput] = useState('');
  const [pimmerSaveMsg, setPimmerSaveMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.getServicesStatus();
      setData(result);
    } catch (err) {
      console.error('Failed to load services status:', err);
      setError('Не удалось загрузить статус сервисов');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadVoyageSettings = useCallback(async () => {
    setIsVoyageLoading(true);
    try {
      const v = await api.getVoyageSettings();
      setVoyageSettings(v);
    } catch (err) {
      console.error('Failed to load voyage settings:', err);
    } finally {
      setIsVoyageLoading(false);
    }
  }, []);

  const loadNotionSettings = useCallback(async () => {
    setIsNotionLoading(true);
    try {
      const n = await api.getNotionSettings();
      setNotionSettings(n);
    } catch (err) {
      console.error('Failed to load notion settings:', err);
    } finally {
      setIsNotionLoading(false);
    }
  }, []);

  const loadOpenAISettings = useCallback(async () => {
    setIsOpenAILoading(true);
    try {
      const o = await api.getOpenAISettings();
      setOpenaiSettings(o);
    } catch (err) {
      console.error('Failed to load OpenAI settings:', err);
    } finally {
      setIsOpenAILoading(false);
    }
  }, []);

  const loadPimmerSettings = useCallback(async () => {
    setIsPimmerLoading(true);
    try {
      const p = await api.getPimmerSettings();
      setPimmerSettings(p);
    } catch (err) {
      console.error('Failed to load Pimmer settings:', err);
    } finally {
      setIsPimmerLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadVoyageSettings();
    loadNotionSettings();
    loadOpenAISettings();
    loadPimmerSettings();
  }, [load, loadVoyageSettings, loadNotionSettings, loadOpenAISettings, loadPimmerSettings]);

  const handleReindex = async () => {
    setReindexing(true);
    setError(null);
    try {
      await api.reindexRag();
      await load();
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setError(msg || 'Не удалось запустить переиндексацию');
    } finally {
      setReindexing(false);
    }
  };

  if (isLoading && !data) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const adminStatus: ServiceStatus = data?.admin?.status === 'ok' ? 'ok' : 'error';
  const helperReachable = data?.helperAi?.reachable ?? false;
  const health = data?.helperAi?.health;
  const rag = data?.helperAi?.rag;

  const notionConfiguredInAdmin = !!(notionSettings?.hasToken && notionSettings?.wikiPageId);
  const notionStatus: ServiceStatus =
    notionConfiguredInAdmin || health?.notion === 'configured' ? 'configured' : helperReachable ? 'missing' : 'error';
  const voyageStatus: ServiceStatus =
    health?.voyage === 'configured' ? 'configured' : health?.voyage ? 'missing' : helperReachable ? 'missing' : 'error';
  const openaiConfiguredInAdmin = !!openaiSettings?.hasApiKey;
  const openaiStatus: ServiceStatus =
    openaiConfiguredInAdmin || health?.openai === 'configured' ? 'configured' : helperReachable ? 'missing' : 'error';
  const helperStatus: ServiceStatus = helperReachable ? 'ok' : data?.helperAi?.error ? 'error' : 'missing';
  const pimmerStatus: ServiceStatus =
    pimmerSettings?.hasServiceToken && pimmerSettings?.apiBaseUrl ? 'configured' : 'missing';

  const handleSaveNotion = async () => {
    if (!notionSettings) return;
    setIsNotionSaving(true);
    setNotionSaveMsg(null);
    try {
      const payload: Parameters<typeof api.updateNotionSettings>[0] = {
        wikiPageId: notionSettings.wikiPageId,
        wikiMaxPages: notionSettings.wikiMaxPages,
      };
      const token = notionTokenInput.trim();
      if (token) payload.token = token;
      const res = await api.updateNotionSettings(payload);
      setNotionSettings(res.settings);
      setNotionTokenInput('');
      setNotionSaveMsg('Сохранено');
      await load();
    } catch (err) {
      console.error('Failed to save notion settings:', err);
      setNotionSaveMsg('Ошибка сохранения');
    } finally {
      setIsNotionSaving(false);
    }
  };

  const handleTestNotion = async () => {
    setIsNotionTesting(true);
    setNotionSaveMsg(null);
    try {
      const res = await api.testNotionSettings();
      setNotionSaveMsg(res.ok ? 'Notion OK' : 'Notion ошибка');
    } catch (err) {
      console.error('Failed to test notion:', err);
      setNotionSaveMsg('Notion test failed');
    } finally {
      setIsNotionTesting(false);
    }
  };

  const handleSaveOpenAI = async () => {
    if (!openaiSettings) return;
    setIsOpenAISaving(true);
    setOpenaiSaveMsg(null);
    try {
      const payload: { apiKey?: string | null } = {};
      const key = openaiApiKeyInput.trim();
      if (key) payload.apiKey = key;
      const res = await api.updateOpenAISettings(payload);
      setOpenaiSettings(res.settings);
      setOpenaiApiKeyInput('');
      setOpenaiSaveMsg('Сохранено');
      await load();
    } catch (err) {
      console.error('Failed to save OpenAI settings:', err);
      setOpenaiSaveMsg('Ошибка сохранения');
    } finally {
      setIsOpenAISaving(false);
    }
  };

  const handleTestOpenAI = async () => {
    setIsOpenAITesting(true);
    setOpenaiSaveMsg(null);
    try {
      const res = await api.testOpenAISettings();
      setOpenaiSaveMsg(res.ok ? 'OpenAI OK' : 'OpenAI ошибка');
    } catch (err) {
      console.error('Failed to test OpenAI:', err);
      setOpenaiSaveMsg('OpenAI test failed');
    } finally {
      setIsOpenAITesting(false);
    }
  };

  const handleSaveVoyage = async () => {
    if (!voyageSettings) return;
    setIsVoyageSaving(true);
    setVoyageSaveMsg(null);
    try {
      const payload: Parameters<typeof api.updateVoyageSettings>[0] = {
        enabled: voyageSettings.enabled,
        useRerank: voyageSettings.useRerank,
        embedModel: voyageSettings.embedModel,
        rerankModel: voyageSettings.rerankModel,
        maxHistoryMessages: voyageSettings.maxHistoryMessages,
        maxContextChars: voyageSettings.maxContextChars,
        maxUserMessageChars: voyageSettings.maxUserMessageChars,
        dialogTimeoutMs: voyageSettings.dialogTimeoutMs,
        retrieverTimeoutMs: voyageSettings.retrieverTimeoutMs,
        openaiTimeoutMs: voyageSettings.openaiTimeoutMs,
      };
      const key = voyageApiKeyInput.trim();
      if (key) payload.apiKey = key;
      const res = await api.updateVoyageSettings(payload);
      setVoyageSettings(res.settings);
      setVoyageApiKeyInput('');
      setVoyageSaveMsg('Сохранено');
      await load();
    } catch (err) {
      console.error('Failed to save voyage settings:', err);
      setVoyageSaveMsg('Ошибка сохранения');
    } finally {
      setIsVoyageSaving(false);
    }
  };

  const handleTestVoyage = async () => {
    setIsVoyageTesting(true);
    setVoyageSaveMsg(null);
    try {
      const res = await api.testVoyageSettings();
      setVoyageSaveMsg(res.ok ? 'Voyage OK' : 'Voyage ошибка');
    } catch (err) {
      console.error('Failed to test voyage:', err);
      setVoyageSaveMsg('Voyage test failed');
    } finally {
      setIsVoyageTesting(false);
    }
  };

  const handleSavePimmer = async () => {
    if (!pimmerSettings) return;
    setIsPimmerSaving(true);
    setPimmerSaveMsg(null);
    try {
      const payload: Parameters<typeof api.updatePimmerSettings>[0] = {
        apiBaseUrl: pimmerSettings.apiBaseUrl,
        versionApp: pimmerSettings.versionApp,
        lang: pimmerSettings.lang,
        os: pimmerSettings.os,
        adminCabinetUrl: pimmerSettings.adminCabinetUrl,
        taskIdRemoveDemoProducts: pimmerSettings.taskIdRemoveDemoProducts,
        defaultIntegrationAppId: pimmerSettings.defaultIntegrationAppId,
      };
      const token = pimmerTokenInput.trim();
      if (token) payload.serviceToken = token;
      const res = await api.updatePimmerSettings(payload);
      setPimmerSettings(res.settings);
      setPimmerTokenInput('');
      setPimmerSaveMsg('Сохранено');
    } catch (err) {
      console.error('Failed to save Pimmer settings:', err);
      setPimmerSaveMsg('Ошибка сохранения');
    } finally {
      setIsPimmerSaving(false);
    }
  };

  const CLEAR_CONFIRM_WORD = 'Очистить';

  const closeClearDialog = () => {
    if (isClearing) return;
    setClearTarget(null);
    setClearConfirmInput('');
  };

  const handleClearConfirm = async () => {
    if (!clearTarget) return;
    if (clearConfirmInput.trim() !== CLEAR_CONFIRM_WORD) return;
    setIsClearing(true);
    setClearMsg(null);
    try {
      if (clearTarget === 'integration-requests') {
        const res = await api.clearIntegrationRequests(clearConfirmInput.trim());
        setClearMsg(`Заявки на интеграцию очищены: удалено ${res.deleted}`);
      } else if (clearTarget === 'users-and-stores') {
        const res = await api.clearUsersAndStores(clearConfirmInput.trim());
        const d = res.deleted;
        setClearMsg(
          `Пользователи и магазины очищены: users=${d.users}, user_stores=${d.user_stores}, ` +
          `store_registry=${d.store_registry}, store_launch_steps=${d.store_launch_steps}`
        );
      } else if (clearTarget === 'subscriptions') {
        const res = await api.clearSubscriptions(clearConfirmInput.trim());
        const d = res.deleted;
        setClearMsg(
          `Подписки очищены: store_subscriptions=${d.store_subscriptions ?? 0}, ` +
          `subscription_audit_log=${d.subscription_audit_log ?? 0}`
        );
      }
      setClearTarget(null);
      setClearConfirmInput('');
    } catch (err) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setClearMsg(msg || 'Не удалось выполнить очистку');
    } finally {
      setIsClearing(false);
    }
  };

  const handleTestPimmer = async () => {
    setIsPimmerTesting(true);
    setPimmerSaveMsg(null);
    try {
      const res = await api.testPimmerSettings();
      setPimmerSaveMsg(res.ok ? `Pimmer OK${res.status ? ` (${res.status})` : ''}` : (res.error || 'Pimmer ошибка'));
    } catch (err) {
      console.error('Failed to test Pimmer:', err);
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setPimmerSaveMsg(msg || 'Pimmer test failed');
    } finally {
      setIsPimmerTesting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Состояние сервисов</h1>
        <Button variant="outline" size="sm" onClick={load} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Приложения — Admin, PWA, MySQL */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <LayoutGrid className="w-4 h-4" />
          Приложения
        </h2>
        <div className="space-y-4">
          <div>
            <h3 className="text-xs font-medium text-muted-foreground mb-2">Admin</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    Admin API
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <StatusBadge status={adminStatus} />
                    <span className="text-xs text-muted-foreground">{data?.admin?.timestamp && formatDate(data.admin.timestamp)}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    Admin Frontend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <StatusBadge status={data?.frontendAdmin?.reachable ? 'ok' : data?.frontendAdmin?.error ? 'error' : 'missing'} />
                  </div>
                  {data?.frontendAdmin?.error && <p className="mt-2 text-xs text-destructive">{data.frontendAdmin.error}</p>}
                </CardContent>
              </Card>
            </div>
          </div>
          <div>
            <h3 className="text-xs font-medium text-muted-foreground mb-2">PWA</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    PWA API
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <StatusBadge status={data?.backend?.reachable ? 'ok' : data?.backend?.error ? 'error' : 'missing'} />
                    <span className="text-xs text-muted-foreground">{data?.backend?.timestamp && formatDate(data.backend.timestamp)}</span>
                  </div>
                  {data?.backend?.error && <p className="mt-2 text-xs text-destructive">{data.backend.error}</p>}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    PWA Frontend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <StatusBadge status={data?.frontendPwa?.reachable ? 'ok' : data?.frontendPwa?.error ? 'error' : 'missing'} />
                  </div>
                  {data?.frontendPwa?.error && <p className="mt-2 text-xs text-destructive">{data.frontendPwa.error}</p>}
                </CardContent>
              </Card>
            </div>
          </div>
          <div>
            <h3 className="text-xs font-medium text-muted-foreground mb-2">База данных</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      MySQL
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMysqlMaintenanceExpanded((v) => !v)}
                    >
                      {mysqlMaintenanceExpanded ? 'Скрыть' : 'Очистка'}
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <StatusBadge status={data?.mysql?.reachable ? 'ok' : data?.mysql?.error ? 'error' : 'missing'} />
                  </div>
                  {data?.mysql?.error && <p className="mt-2 text-xs text-destructive">{data.mysql.error}</p>}
                  {mysqlMaintenanceExpanded && (
                    <div className="mt-3 space-y-3 border-t pt-3">
                      <p className="text-xs text-muted-foreground">
                        Удаляет все записи из таблиц. Действие необратимо — требует подтверждения вводом
                        слова «{CLEAR_CONFIRM_WORD}».
                      </p>
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => { setClearTarget('integration-requests'); setClearConfirmInput(''); setClearMsg(null); }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Очистить «Заявки на интеграцию»
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => { setClearTarget('users-and-stores'); setClearConfirmInput(''); setClearMsg(null); }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Очистить «Пользователи и магазины»
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => { setClearTarget('subscriptions'); setClearConfirmInput(''); setClearMsg(null); }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Очистить «Подписки»
                        </Button>
                      </div>
                      {clearMsg && <p className="text-xs text-muted-foreground">{clearMsg}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* AI и RAG — Helper AI, RAG Index */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Brain className="w-4 h-4" />
          AI и RAG
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="w-4 h-4" />
                Helper AI
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <StatusBadge status={helperStatus} />
              </div>
              {data?.helperAi?.error && (
                <p className="mt-2 text-xs text-destructive">{data.helperAi.error}</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-4 h-4" />
                RAG Index
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {rag ? (
                <>
                  <div className="flex items-center gap-2">
                  {rag.running ? (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Индексация...
                    </Badge>
                  ) : (
                    <StatusBadge status={rag.last_error ? 'error' : 'ok'} />
                  )}
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  {rag.last_pages != null && <p>Страниц: {rag.last_pages}</p>}
                  {rag.last_chunks != null && <p>Чанков: {rag.last_chunks}</p>}
                  {rag.last_finished_at && <p>Обновлён: {formatDate(rag.last_finished_at)}</p>}
                  {rag.last_error && <p className="text-destructive">{rag.last_error}</p>}
                </div>
              </>
            ) : (
              <StatusBadge status={helperReachable ? 'missing' : 'error'} />
            )}
          </CardContent>
          </Card>
        </div>
      </section>

      {/* Сторонние сервисы */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Сторонние сервисы
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <span>Notion</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNotionExpanded((v) => !v)}
                >
                  {notionExpanded ? 'Скрыть' : 'Настроить'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StatusBadge status={notionStatus} />
              {notionExpanded && (
                <div className="mt-3 space-y-3">
                  {isNotionLoading && (
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Загрузка настроек...
                    </div>
                  )}
                  {notionSettings && (
                    <>
                      <div className="space-y-1">
                        <Label>NOTION_TOKEN</Label>
                        <Input
                          value={notionTokenInput}
                          placeholder={notionSettings.hasToken ? notionSettings.token : 'не задан'}
                          onChange={(e) => setNotionTokenInput(e.target.value)}
                          autoComplete="off"
                        />
                        <p className="text-xs text-muted-foreground">
                          Оставьте пустым, чтобы не менять токен.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label>NOTION_WIKI_PAGE_ID</Label>
                        <Input
                          value={notionSettings.wikiPageId}
                          onChange={(e) =>
                            setNotionSettings({
                              ...notionSettings,
                              wikiPageId: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>wikiMaxPages</Label>
                        <Input
                          type="number"
                          value={notionSettings.wikiMaxPages ?? ''}
                          onChange={(e) =>
                            setNotionSettings({
                              ...notionSettings,
                              wikiMaxPages: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={handleSaveNotion} disabled={isNotionSaving}>
                          {isNotionSaving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Сохранение...
                            </>
                          ) : (
                            'Сохранить'
                          )}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleTestNotion} disabled={isNotionTesting}>
                          {isNotionTesting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Проверка...
                            </>
                          ) : (
                            'Проверить'
                          )}
                        </Button>
                        {notionSaveMsg && <span className="text-xs text-muted-foreground">{notionSaveMsg}</span>}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <span>Voyage AI</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setVoyageExpanded((v) => !v)}
                >
                  {voyageExpanded ? 'Скрыть' : 'Настроить'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StatusBadge status={voyageStatus} />
              {voyageExpanded && (
                <div className="mt-3 space-y-3">
                  {isVoyageLoading && (
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Загрузка настроек...
                    </div>
                  )}
                  {voyageSettings && (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          id="voyageEnabled"
                          type="checkbox"
                          className="h-4 w-4"
                          checked={voyageSettings.enabled}
                          onChange={(e) => setVoyageSettings({ ...voyageSettings, enabled: e.target.checked })}
                        />
                        <Label htmlFor="voyageEnabled">Включить Voyage</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          id="voyageUseRerank"
                          type="checkbox"
                          className="h-4 w-4"
                          checked={voyageSettings.useRerank}
                          onChange={(e) => setVoyageSettings({ ...voyageSettings, useRerank: e.target.checked })}
                        />
                        <Label htmlFor="voyageUseRerank">Использовать rerank</Label>
                      </div>

                      <div className="space-y-1">
                        <Label>API key</Label>
                        <Input
                          value={voyageApiKeyInput}
                          placeholder={voyageSettings.hasApiKey ? voyageSettings.apiKey : 'не задан'}
                          onChange={(e) => setVoyageApiKeyInput(e.target.value)}
                          autoComplete="off"
                        />
                        <p className="text-xs text-muted-foreground">Оставьте пустым, чтобы не менять ключ.</p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>Embed model</Label>
                          <Input
                            value={voyageSettings.embedModel}
                            onChange={(e) => setVoyageSettings({ ...voyageSettings, embedModel: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Rerank model</Label>
                          <Input
                            value={voyageSettings.rerankModel}
                            onChange={(e) => setVoyageSettings({ ...voyageSettings, rerankModel: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>maxContextChars</Label>
                          <Input
                            type="number"
                            value={voyageSettings.maxContextChars ?? ''}
                            onChange={(e) =>
                              setVoyageSettings({
                                ...voyageSettings,
                                maxContextChars: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>dialogTimeoutMs</Label>
                          <Input
                            type="number"
                            value={voyageSettings.dialogTimeoutMs ?? ''}
                            onChange={(e) =>
                              setVoyageSettings({
                                ...voyageSettings,
                                dialogTimeoutMs: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>retrieverTimeoutMs</Label>
                          <Input
                            type="number"
                            value={voyageSettings.retrieverTimeoutMs ?? ''}
                            onChange={(e) =>
                              setVoyageSettings({
                                ...voyageSettings,
                                retrieverTimeoutMs: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>openaiTimeoutMs</Label>
                          <Input
                            type="number"
                            value={voyageSettings.openaiTimeoutMs ?? ''}
                            onChange={(e) =>
                              setVoyageSettings({
                                ...voyageSettings,
                                openaiTimeoutMs: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>maxHistoryMessages</Label>
                          <Input
                            type="number"
                            value={voyageSettings.maxHistoryMessages ?? ''}
                            onChange={(e) =>
                              setVoyageSettings({
                                ...voyageSettings,
                                maxHistoryMessages: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>maxUserMessageChars</Label>
                          <Input
                            type="number"
                            value={voyageSettings.maxUserMessageChars ?? ''}
                            onChange={(e) =>
                              setVoyageSettings({
                                ...voyageSettings,
                                maxUserMessageChars: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={handleSaveVoyage} disabled={isVoyageSaving}>
                          {isVoyageSaving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Сохранение...
                            </>
                          ) : (
                            'Сохранить'
                          )}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleTestVoyage} disabled={isVoyageTesting}>
                          {isVoyageTesting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Проверка...
                            </>
                          ) : (
                            'Проверить'
                          )}
                        </Button>
                        {voyageSaveMsg && <span className="text-xs text-muted-foreground">{voyageSaveMsg}</span>}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <span>OpenAI</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenaiExpanded((v) => !v)}
                >
                  {openaiExpanded ? 'Скрыть' : 'Настроить'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StatusBadge status={openaiStatus} />
              {openaiExpanded && (
                <div className="mt-3 space-y-3">
                  {isOpenAILoading && (
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Загрузка настроек...
                    </div>
                  )}
                  {openaiSettings && (
                    <>
                      <div className="space-y-1">
                        <Label>OPENAI_API_KEY</Label>
                        <Input
                          value={openaiApiKeyInput}
                          placeholder={openaiSettings.hasApiKey ? openaiSettings.apiKey || 'не задан' : 'не задан'}
                          onChange={(e) => setOpenaiApiKeyInput(e.target.value)}
                          autoComplete="off"
                        />
                        <p className="text-xs text-muted-foreground">
                          Оставьте пустым, чтобы не менять ключ.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={handleSaveOpenAI} disabled={isOpenAISaving}>
                          {isOpenAISaving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Сохранение...
                            </>
                          ) : (
                            'Сохранить'
                          )}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleTestOpenAI} disabled={isOpenAITesting}>
                          {isOpenAITesting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Проверка...
                            </>
                          ) : (
                            'Проверить'
                          )}
                        </Button>
                        {openaiSaveMsg && <span className="text-xs text-muted-foreground">{openaiSaveMsg}</span>}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <span>Pimmer API</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPimmerExpanded((v) => !v)}
                >
                  {pimmerExpanded ? 'Скрыть' : 'Настроить'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StatusBadge status={pimmerStatus} />
              {pimmerExpanded && (
                <div className="mt-3 space-y-3">
                  {isPimmerLoading && (
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Загрузка настроек...
                    </div>
                  )}
                  {pimmerSettings && (
                    <>
                      <div className="space-y-1">
                        <Label>SERVICE_TOKEN</Label>
                        <Input
                          value={pimmerTokenInput}
                          placeholder={pimmerSettings.hasServiceToken ? pimmerSettings.serviceToken : 'не задан'}
                          onChange={(e) => setPimmerTokenInput(e.target.value)}
                          autoComplete="off"
                        />
                        <p className="text-xs text-muted-foreground">
                          Оставьте пустым, чтобы не менять токен.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label>PIMMER_API_BASE_URL</Label>
                        <Input
                          value={pimmerSettings.apiBaseUrl}
                          onChange={(e) =>
                            setPimmerSettings({ ...pimmerSettings, apiBaseUrl: e.target.value })
                          }
                          placeholder="https://admin.pimmer.dev/api/v1.0"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>ADMIN_CABINET_URL</Label>
                        <Input
                          value={pimmerSettings.adminCabinetUrl}
                          onChange={(e) =>
                            setPimmerSettings({ ...pimmerSettings, adminCabinetUrl: e.target.value })
                          }
                          placeholder="https://pimmer.dev"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>TASK_ID_REMOVE_DEMO_PRODUCTS</Label>
                        <Input
                          value={pimmerSettings.taskIdRemoveDemoProducts}
                          onChange={(e) =>
                            setPimmerSettings({ ...pimmerSettings, taskIdRemoveDemoProducts: e.target.value })
                          }
                          placeholder="fd080507-b22f-4b8e-859e-223748dc7a21"
                        />
                        <p className="text-xs text-muted-foreground">
                          UUID задачи Pimmer для удаления демо-товаров (PWA вкладка «Товары»).
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label>DEFAULT_INTEGRATION_APP_ID</Label>
                        <select
                          value={pimmerSettings.defaultIntegrationAppId}
                          onChange={(e) =>
                            setPimmerSettings({ ...pimmerSettings, defaultIntegrationAppId: e.target.value })
                          }
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        >
                          <option value="">— не выбрано (Pimmer подберёт по типу магазина) —</option>
                          {DISTRIBUTION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                          {pimmerSettings.defaultIntegrationAppId &&
                            !DISTRIBUTION_OPTIONS.some((opt) => opt.value === pimmerSettings.defaultIntegrationAppId) && (
                              <option value={pimmerSettings.defaultIntegrationAppId}>
                                {pimmerSettings.defaultIntegrationAppId} (нет в списке)
                              </option>
                            )}
                        </select>
                        <p className="text-xs text-muted-foreground">
                          Дистрибуция (integration_apps на pimmer.dev), в которую автоматически
                          попадают новые магазины при создании в PWA. «Не выбрано» — Pimmer
                          сам выбирает дистрибуцию по типу магазина.
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label>VERSION_APP</Label>
                          <Input
                            value={pimmerSettings.versionApp}
                            onChange={(e) =>
                              setPimmerSettings({ ...pimmerSettings, versionApp: e.target.value })
                            }
                            placeholder="3.40"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>LANG</Label>
                          <Input
                            value={pimmerSettings.lang}
                            onChange={(e) =>
                              setPimmerSettings({ ...pimmerSettings, lang: e.target.value })
                            }
                            placeholder="rus"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>OS</Label>
                          <Input
                            value={pimmerSettings.os}
                            onChange={(e) =>
                              setPimmerSettings({ ...pimmerSettings, os: e.target.value })
                            }
                            placeholder="wiki"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={handleSavePimmer} disabled={isPimmerSaving}>
                          {isPimmerSaving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Сохранение...
                            </>
                          ) : (
                            'Сохранить'
                          )}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleTestPimmer} disabled={isPimmerTesting}>
                          {isPimmerTesting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Проверка...
                            </>
                          ) : (
                            'Проверить'
                          )}
                        </Button>
                        {pimmerSaveMsg && <span className="text-xs text-muted-foreground">{pimmerSaveMsg}</span>}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Действия */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Wrench className="w-4 h-4" />
          Действия
        </h2>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Переиндексация Notion → Voyage</CardTitle>
            <p className="text-sm text-muted-foreground">
              Запускает полную переиндексацию страниц Wiki из Notion в векторный индекс Voyage AI.
            </p>
          </CardHeader>
          <CardContent>
            <Button onClick={handleReindex} disabled={reindexing || !helperReachable}>
              {reindexing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Переиндексация...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Переиндексировать
                </>
              )}
            </Button>
          </CardContent>
        </Card>

      </section>

      {clearTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          onClick={closeClearDialog}
        >
          <div
            className="bg-background border rounded-lg shadow-lg max-w-md w-full mx-4 p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Подтверждение очистки"
          >
            <h3 className="text-lg font-semibold">
              {clearTarget === 'integration-requests'
                ? 'Очистить «Заявки на интеграцию»?'
                : clearTarget === 'users-and-stores'
                ? 'Очистить «Пользователи и магазины»?'
                : 'Очистить «Подписки»?'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {clearTarget === 'integration-requests'
                ? 'Будут удалены все записи из таблицы integration_requests. Это действие необратимо.'
                : clearTarget === 'users-and-stores'
                ? 'Будут удалены все записи из таблиц users, user_stores, store_registry, store_launch_steps. Чаты поддержки не затрагиваются. Это действие необратимо.'
                : 'Будут удалены все записи из таблиц store_subscriptions и subscription_audit_log. Тарифы (subscription_tariffs) сохраняются. Это действие необратимо.'}
            </p>
            <div className="space-y-2">
              <Label htmlFor="clear-confirm-input">
                Для подтверждения введите <code className="px-1 py-0.5 rounded bg-muted">{CLEAR_CONFIRM_WORD}</code>
              </Label>
              <Input
                id="clear-confirm-input"
                value={clearConfirmInput}
                onChange={(e) => setClearConfirmInput(e.target.value)}
                autoFocus
                disabled={isClearing}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeClearDialog} disabled={isClearing}>
                Отмена
              </Button>
              <Button
                variant="destructive"
                onClick={handleClearConfirm}
                disabled={isClearing || clearConfirmInput.trim() !== CLEAR_CONFIRM_WORD}
              >
                {isClearing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Очистка...
                  </>
                ) : (
                  'Очистить'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
